import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PersonaRoutingService } from './persona-routing.service';

export type NavigationTarget = 'profile' | 'home' | 'contacts' | 'growers' | 'admin' | 'history' | 'comments';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  
  constructor(
    private readonly router: Router,
    private readonly personaRouting: PersonaRoutingService
  ) {}

  /**
   * Navigates to a specific route based on the navigation target
   * @param target The navigation target
   * @param passportId Optional passport ID for history/comments routes
   * @param header Optional header data for history/comments routes
   */
  navigateTo(target: NavigationTarget, passportId?: string, header?: any): void {
    // Special handling for passport-specific routes - use state instead of URL params
    if (target === 'history' && passportId) {
      this.router.navigate(['/history'], { state: { passportId, header } });
      return;
    }
    if (target === 'comments' && passportId) {
      this.router.navigate(['/comments'], { state: { passportId, header } });
      return;
    }

    // Standard navigation routes
    const routes: Record<NavigationTarget, string> = {
      profile: '/profile',
      home: '', // Will be determined by current persona
      contacts: '/contacts',
      growers: '/growers',
      admin: '/admin',
      history: '/history',
      comments: '/comments'
    };

    const route = routes[target];
    if (route) {
      this.router.navigateByUrl(route);
    } else if (target === 'home') {
      // Use current persona to determine home route
      const persona = localStorage.getItem('persona') || 'grower';
      const homeRoute = this.personaRouting.personaToRoute(persona);
      this.router.navigateByUrl(homeRoute);
    } else {
      console.warn(`Unknown navigation target: ${target}`);
      const persona = localStorage.getItem('persona') || 'grower';
      const fallbackRoute = this.personaRouting.personaToRoute(persona);
      this.router.navigateByUrl(fallbackRoute);
    }
  }

  /**
   * Handles standard navigation change events from navbar components
   * Uses PersonaRoutingService for smart persona-aware navigation
   */
  onNavChange(id: string, passportId?: string): void {
    if (passportId) {
      const target = (id || '').toLowerCase() as NavigationTarget;
      this.navigateTo(target, passportId);
    } else {
      // Use PersonaRoutingService for standard nav without passport ID
      const persona = localStorage.getItem('persona') || 'grower';
      this.personaRouting.handleNavigation(id, persona);
    }
  }

  /**
   * Navigates back using browser history
   */
  goBack(): void {
    globalThis.history.back();
  }

  /**
   * Navigates to passport details page
   * @param passportId The passport ID
   * @param state Optional state data (e.g., { from: 'receiver' })
   */
  openPassportDetails(passportId: string, state?: { from?: string }): void {
    this.router.navigate(['/passport', passportId], { state });
  }

  /**
   * Navigates to comments for a specific passport
   * @param passportId The passport ID
   * @param header Optional header data for the comments page
   */
  openComments(passportId: string, header?: any): void {
    this.router.navigate(['/comments'], { state: { passportId, header } });
  }

  /**
   * Navigates to history for a specific passport
   * @param passportId The passport ID
   * @param header Optional header data for the history page
   */
  openHistory(passportId: string, header?: any): void {
    this.router.navigate(['/history'], { state: { passportId, header } });
  }

  /**
   * Gets the current route URL
   */
  getCurrentUrl(): string {
    return this.router.url;
  }

  /**
   * Checks if the current route matches a given path
   */
  isCurrentRoute(path: string): boolean {
    return this.router.url === path;
  }
}